/**
 * "Set up with Bailey": the chapters, in order, for each kind of person.
 *
 * Minutes are hands-on time only. Bailey reads files and Beu approves the
 * business in the background, so the numbers are small and still true.
 * They are deliberately on the short side (Ben, 25 Sept 2026): people start
 * what they believe they can finish.
 *
 * `applies(answers)` hides a chapter that has nothing to do with this
 * business, so a solo barber never sees the team steps or money rules.
 */

const about = (a) => (a && a.about) || {};
export const hasSelfEmployed = (a) => ['selfEmployed', 'mix'].includes(about(a).team);
export const isSolo = (a) => about(a).team === 'solo';
export const takesDeposits = (a) => ['deposits', 'cardHeld'].includes(about(a).deposits);

/**
 * Treatment rooms or shared equipment: what chapter 1 said. When it wasn't
 * asked (no beauty, nails or spa trade picked), a spa still needs them.
 */
export const needsRooms = (a, businessType) => {
  const r = about(a).rooms;
  if (r === 'yes') return true;
  if (r === 'no') return false;
  return (about(a).trades || []).includes('spa') || businessType === 'spa';
};

/**
 * Charges chosen before card payments were live, held in setup rather than
 * switched on (a client can't be charged without Stripe). Getting paid
 * offers to switch them on once payments are live, then clears them.
 *   answers.menu.depositPlan   { mode: 'percent' | 'amount', value, at }
 *   answers.page.policyPlan    { choice, tiers, noShow, at }
 */
export const heldCharges = (answers) => {
  const menu = (answers && answers.menu) || {};
  const page = (answers && answers.page) || {};
  const deposits = menu.depositPlan || null;
  const policy = page.policyPlan || null;
  return { deposits, policy, any: !!(deposits || policy) };
};

export const OWNER_CHAPTERS = [
  {
    id: 'about',
    title: 'Your business',
    long: 'Tell Bailey about your business',
    minutes: 1,
    bailey: 'Before we set anything up, tell me how your place works. A few quick taps, and I’ll only show you the steps that apply to you.',
  },
  {
    id: 'files',
    title: 'Bring everything in',
    long: 'Bring everything in',
    minutes: 1,
    bailey: 'Send me whatever you’ve got from your old system: exports, spreadsheets, even a photo of your price list. I’ll read it while we carry on.',
    // A brand-new business has nothing to bring. The starter menu and adding
    // people by hand take over in the chapters after.
    applies: (a) => about(a).from !== 'new',
  },
  {
    id: 'team',
    title: 'Team and hours',
    long: 'Your team and hours',
    soloTitle: 'Your hours',
    minutes: 3,
    bailey: 'Now your team. Employed or self-employed can’t be changed later, and each self-employed stylist adds to your plan, so I’ll always show the cost.',
    soloBailey: 'Now your hours, so clients can only book you when you’re in.',
  },
  {
    id: 'menu',
    title: 'Your menu',
    long: 'Your menu',
    minutes: 2,
    bailey: 'Now your services. Then we’ll put each one on the right people, at the right price.',
  },
  {
    id: 'money',
    title: 'Money rules',
    long: 'Money rules',
    minutes: 2,
    bailey: 'A few rules that decide where money goes with self-employed stylists. There’s no wrong answer, and you can change any of them later in Settings.',
    // Only salons with self-employed stylists have these choices. Deposits
    // are set per service in the menu, and the cancellation policy every
    // salon needs is in the booking page chapter.
    applies: (a) => hasSelfEmployed(a),
  },
  {
    id: 'paid',
    title: 'Getting paid',
    long: 'Getting paid',
    minutes: 5,
    bailey: 'Beu checks every new business before card payments switch on. It usually takes a working day, and I’ll let you know here once you’re approved.',
  },
  {
    id: 'clients',
    title: 'Your clients',
    long: 'Your clients',
    minutes: 1,
    bailey: 'Let’s make sure your clients came across cleanly. Nothing is ever sent to them from here.',
  },
  {
    id: 'page',
    title: 'Booking page',
    long: 'Your booking page and messages',
    minutes: 3,
    bailey: 'This is what your clients will see. Let’s make it look like you, and check what they’ll get after they book.',
  },
  // The rehearsal (a practice run in the real diary) was dropped on 30 Sept
  // 2026 (Ben), and its Go live rows with it. chapters/Rehearsal.js stays for
  // now, unreachable, in case it comes back.
  {
    id: 'ready',
    title: 'Go live',
    long: 'Am I ready? Then go live',
    minutes: 1,
    bailey: 'Let me check everything that matters before your page goes live.',
  },
];

// An invited self-employed stylist joining a salon: their own short plan.
export const STYLIST_CHAPTERS = [
  { id: 'profile', title: 'Your profile', long: 'Your profile', minutes: 1, bailey: 'Hi, I’m Bailey. Let’s get you ready to take bookings here.' },
  { id: 'services', title: 'Your services', long: 'Your services and prices', minutes: 2, bailey: 'Which services do you do, and at what price? Your prices are yours.' },
  { id: 'hours', title: 'Your hours', long: 'Your hours', minutes: 1, bailey: 'When are you in? Clients can only book you then.' },
  { id: 'paid', title: 'Getting paid', long: 'Getting paid', minutes: 2, bailey: 'Connect your own payments account, and your share of every card payment lands with you at the till.' },
  { id: 'privacy', title: 'Your privacy', long: 'Your privacy', minutes: 1, bailey: 'Who can see your clients and your diary is your choice. Let’s set it.' },
  { id: 'link', title: 'Your link', long: 'Your link and rules', minutes: 1, bailey: 'Your own booking link, and what happens when someone cancels late.' },
  { id: 'ready', title: 'Ready', long: 'Ready to take bookings', minutes: 0, bailey: 'Let me check you’re all set.' },
];

// Employed staff: a quick hello, nothing about money.
export const STAFF_CHAPTERS = [
  { id: 'profile', title: 'Your profile', long: 'Your profile', minutes: 1, bailey: 'Hi, I’m Bailey. Welcome aboard.' },
  { id: 'app', title: 'The app', long: 'Beu on your phone', minutes: 1, bailey: 'Your diary goes where you go. Get the app on your phone.' },
];

/**
 * Adding a location: the `location` track.
 *
 * Before the location exists (role 'location', rendered by the add-location
 * page, not by SetupHost): where it is, what comes across, then the plan.
 * The state is the location intent on the server, so it resumes anywhere.
 * The plan step is its own page (/add-location/plan): nothing is created
 * until billing is confirmed.
 */
export const LOCATION_CHAPTERS = [
  { id: 'newLocation', title: 'Where it is', long: 'Name and address', minutes: 1, bailey: 'Opening somewhere new? Let’s start with where it is.' },
  { id: 'bringOver', title: 'Bring over', long: 'What to bring over', minutes: 1, bailey: 'What should the new place start with? You can change any of it later.' },
  { id: 'plan', title: 'Your plan', long: 'Add it to your plan', minutes: 1, bailey: 'Here’s what it costs, before anything is charged.' },
];

/**
 * Once it's billed and created, Bailey carries on AT the new site (the
 * owner's plan there, state.track 'location'): the owner chapters that act
 * on the active site, with their own lines. Not "Your business" or "Bring
 * everything in": that was answered when the business joined.
 */
const LOCATION_SITE_LINES = {
  team: 'Who works here, and when? Hours are set per location, so a stylist can split their week between sites.',
  menu: 'Your menu is here. Prices can be different at each location, so check them for this one.',
  paid: 'Card payments here go to the same account as your other location, with the split shown by location. Let’s get the till ready.',
  page: 'This location gets its own booking page. Let’s make sure it looks right and says where to find you.',
  ready: 'Let me check everything this location needs before its page goes live.',
};
export const LOCATION_SITE_CHAPTER_IDS = ['team', 'menu', 'paid', 'page', 'ready'];

export function chaptersFor(role, answers, track) {
  if (role === 'location') return LOCATION_CHAPTERS;
  if (role === 'owner' && track === 'location') {
    return OWNER_CHAPTERS
      .filter((c) => LOCATION_SITE_CHAPTER_IDS.includes(c.id))
      .map((c) => {
        const solo = isSolo(answers) && c.soloTitle;
        return { ...c, ...(solo ? { title: c.soloTitle, long: c.soloTitle } : {}), bailey: LOCATION_SITE_LINES[c.id] || c.bailey };
      });
  }
  const list = role === 'stylist' ? STYLIST_CHAPTERS : role === 'staff' ? STAFF_CHAPTERS : OWNER_CHAPTERS;
  return list
    .filter((c) => !c.applies || c.applies(answers || {}))
    .map((c) => (role === 'owner' && isSolo(answers) && c.soloTitle
      ? { ...c, title: c.soloTitle, long: c.soloTitle, bailey: c.soloBailey || c.bailey }
      : c));
}

export const totalMinutes = (chapters) => chapters.reduce((n, c) => n + (c.minutes || 0), 0);

/**
 * The rehearsal's practice tasks, done in the real app while setup is
 * closed. book, move and pay are FIRST_TASKS keys; 'client' is the key
 * Clients.js reports when a client record is opened.
 *
 * They're only counted while the rehearsal is "armed" (the owner tapped one
 * of these buttons, within the last few hours): SetupContext listens then,
 * and never otherwise, so an everyday booking can't tick one.
 */
export const PRACTICE_TASKS = [
  {
    key: 'book',
    title: 'Book the practice client in',
    how: 'Tap an empty space in the diary, pick Sample Client (practice) and any service. It’s the move you’ll make most.',
    pill: 'Book the practice client in',
    cta: 'Go to the diary',
  },
  {
    key: 'move',
    title: 'Move a booking',
    how: 'Drag the practice booking to another time, then confirm. If Beu offers to tell the client, nothing is sent: they have no contact details.',
    pill: 'Move the practice booking to another time',
    cta: 'Go to the diary',
  },
  {
    key: 'pay',
    title: 'Check out the practice booking',
    how: 'Open it, tap Checkout and choose Cash, so no money moves.',
    pill: 'Check out the practice booking with Cash',
    cta: 'Open the booking',
  },
  {
    key: 'client',
    title: 'Look inside a client record',
    how: 'Open the practice client in Clients. Bookings, notes, forms and spend all sit in one place.',
    pill: 'Open the practice client’s record',
    cta: 'Open Clients',
  },
];
export const REHEARSAL_STEPS = ['prepare', 'diary', 'self'];
export const PRACTICE_ARM_MS = 6 * 36e5;
/** The rehearsal's saved step while they're in the diary. */
export const practiceDiaryStep = (doneCount, pill) => ({
  id: 'diary',
  i: 1 + doneCount / PRACTICE_TASKS.length,
  n: REHEARSAL_STEPS.length,
  ...(pill ? { pill } : {}),
});

/** "About 12 minutes left", never "about 0". */
export function minutesLeftLabel(mins) {
  if (mins <= 0) return 'Nearly there';
  if (mins === 1) return 'About a minute left';
  return `About ${mins} minutes left`;
}
